import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Calendar, ChevronRight, Search, ExternalLink } from 'lucide-react'
import { newsData, NewsItem } from '../data/newsData'

const NewsPage = () => {
  const [activeCategory, setActiveCategory] = useState<string>('全部')
  const categories = ['全部', '项目案例', '公司新闻', '行业资讯', '技术动态', '媒体报道', '产品发布']

  const filteredNews = activeCategory === '全部'
    ? newsData
    : newsData.filter(news => news.category === activeCategory)

  const sortedNews = [...filteredNews].sort((a, b) => 
    new Date(b.date).getTime() - new Date(a.date).getTime()
  )

  return (
    <div className="min-h-screen pt-20">
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="section-title">新闻动态</h1>
            <p className="text-white/70 text-lg max-w-3xl mx-auto mb-8">
              了解音智科技最新资讯、行业动态和技术前沿
            </p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="flex flex-wrap justify-center gap-3"
            >
              {categories.map((category) => (
                <motion.button
                  key={category}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setActiveCategory(category)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                    activeCategory === category
                      ? 'bg-gradient-to-r from-tech-500 to-tech-400 text-white shadow-lg shadow-tech-500/30'
                      : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                  }`}
                >
                  {category}
                </motion.button>
              ))}
            </motion.div>
          </motion.div>

          <div className="space-y-4">
            <AnimatePresence mode="wait">
              {sortedNews.map((news, index) => (
                <NewsCard key={news.id} news={news} index={index} />
              ))}
            </AnimatePresence>
          </div>

          {sortedNews.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <Search className="w-16 h-16 text-white/30 mx-auto mb-4" />
              <p className="text-white/50">暂无新闻</p>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  )
}

const NewsCard = ({ news, index }: { news: NewsItem; index: number }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <motion.div
      initial={{ opacity: 0, x: -50 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05, duration: 0.5 }}
      layout
      className="tech-card overflow-hidden cursor-pointer"
      onClick={() => setIsExpanded(!isExpanded)}
    >
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${news.color} flex items-center justify-center`}>
              <news.icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <span className={`px-2.5 py-0.5 rounded text-xs font-medium bg-gradient-to-r ${news.color} text-white`}>
                  {news.category}
                </span>
                <div className="flex items-center text-white/40 text-xs">
                  <Calendar className="w-3 h-3 mr-1" />
                  {news.date}
                </div>
              </div>
            </div>
          </div>
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.3 }}
            className="text-white/40"
          >
            <ChevronRight className="w-5 h-5" />
          </motion.div>
        </div>

        <a
          href={news.link}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="block group/title"
        >
          <h3 className="text-xl font-bold text-white mb-3 hover:text-tech-400 transition-colors group-hover/title:underline">
            {news.title}
          </h3>
        </a>

        <p className="text-white/60 mb-4 line-clamp-2">
          {news.excerpt}
        </p>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="border-t border-white/10 pt-4 mt-4">
                <p className="text-white/70 leading-relaxed mb-4">
                  {news.content}
                </p>
                
                {news.link && (
                  <motion.a
                    href={news.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="inline-flex items-center space-x-2 tech-button text-sm"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>阅读原文</span>
                  </motion.a>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {!isExpanded && news.link && (
          <motion.a
            href={news.link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            whileHover={{ x: 5 }}
            className="flex items-center text-tech-400 text-sm font-medium mt-2"
          >
            <span>阅读全文</span>
            <ExternalLink className="w-4 h-4 ml-1" />
          </motion.a>
        )}
      </div>
    </motion.div>
  )
}

export default NewsPage
